// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/apartmentPhoto.ts
// 🔧 PURPOSE: La foto principale dell'appartamento prenotato, servita
//             piccola, per l'intestazione della Home.
//
//             Il channel manager manda l'originale (booking/info →
//             apartmentPhotoUrl: la prima foto della galleria, su Vercel
//             Blob). L'originale pesa 100 KB – 2,4 MB e puo' portarsi dietro
//             i dati EXIF del telefono: l'app non lo carica mai. Passa invece
//             dall'ottimizzatore di immagini del channel manager
//             (/_next/image: WebP, ridimensionata, metadati tolti), con gli
//             stessi parametri dell'hero del tag NFC (q=60), cosi' la cache
//             di Vercel e' gia' calda: 20–30 KB su un telefono normale.
//
//             Larghezze: 640 / 828 / 1080, tutte fra quelle di serie di Next
//             (deviceSizes); l'intestazione e' larga quanto lo schermo.
//             Solo indirizzi https: il resto (vuoto, http, data:, relativo)
//             da' null e l'intestazione resta marrone come prima.
// ============================================

// Il channel manager dalla configurazione (senza dipendenze), non da
// guestApi: l'intestazione non deve tirarsi dietro i18n e le chiamate API.
import { BOOKING_CONFIG } from '../config/booking';

/** Le larghezze offerte al browser (deviceSizes di Next). */
export const APARTMENT_PHOTO_WIDTHS = [640, 828, 1080] as const;
/** Sotto il velo espresso la qualità piena non si vedrebbe. */
export const APARTMENT_PHOTO_QUALITY = 60;
/** Per i browser che ignorano srcset: una misura da telefono (390px a 2x). */
const FALLBACK_WIDTH = 828;
/** A tutta larghezza. */
export const APARTMENT_PHOTO_SIZES = '100vw';

export interface ApartmentPhotoProps {
  src: string;
  srcSet: string;
  sizes: string;
}

/** L'indirizzo dell'ottimizzatore del channel manager per una larghezza. */
const resized = (url: string, width: number): string =>
  `${BOOKING_CONFIG.BASE_URL}/_next/image?url=${encodeURIComponent(url)}&w=${width}&q=${APARTMENT_PHOTO_QUALITY}`;

/** src / srcSet / sizes per l'<img> della foto, o null se non c'è una foto https. */
export function apartmentPhotoProps(url?: string | null): ApartmentPhotoProps | null {
  const raw = typeof url === 'string' ? url.trim() : '';
  if (!/^https:\/\/[^\s/]+/i.test(raw)) return null;
  return {
    src: resized(raw, FALLBACK_WIDTH),
    srcSet: APARTMENT_PHOTO_WIDTHS.map((w) => `${resized(raw, w)} ${w}w`).join(', '),
    sizes: APARTMENT_PHOTO_SIZES,
  };
}
